import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/shared/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // 主题中立的默认样式:用 design token,跨三套皮肤都可读。
      // cyber 的霓虹底/边/辉光在 skin-cyber.css `[data-skin="cyber"] [role="tablist"]`
      // 里用 !important 还原;comic/standard 直接用这里的浅底 token。
      "cyber-tabs-list inline-flex h-10 items-center justify-center rounded-lg p-1",
      "bg-muted/60 backdrop-blur-md",
      "border border-border text-muted-foreground",
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "cyber-tab-trigger relative inline-flex items-center justify-center whitespace-nowrap",
      // 注意:不要用 `tracking-wide` —— comic 皮肤的 slider 规则 `[class*="track"]`
      // 会误命中含 "track" 子串的类,把激活态 tab 背景覆成深色 → 白字看不清。
      "rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-300",
      "ring-offset-background uppercase",
      // 中立激活态:浅底深字,三皮肤都可读。cyber 霓虹激活态由
      // skin-cyber.css `[data-skin="cyber"] [role="tab"][data-state="active"]`(!important)还原,
      // comic 粉色激活态由 skin-comic.css 还原。
      "data-[state=active]:bg-background data-[state=active]:text-foreground",
      "data-[state=active]:shadow-sm",
      "hover:text-foreground",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
